import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { AbunanceSelection } from '@resistapp/client/data-utils/filter-data/filter';
import { isNonQuantifiedFlag, isUnclear, isUnquantified } from '@resistapp/common/flag-utils';
import { isAyNormalisationMode } from '@resistapp/common/normalisation-mode';
import { Flags, NormalisationMode } from '@resistapp/common/types';
import { TRACES_VAL } from '@resistapp/common/utils';

const QNT_COLORS = ['#550b1d', '#933b41', '#cc7878', '#dab9b7', '#cfd3ed', '#d6e7f2'];
export const TRACE_COLOR = '#f2f2f2';
export const UNQUANTIFIED_COLOR = TRACE_COLOR;
export const UNCLEAR_COLOR = '#f2d8bd';
export const NOT_DETECTED_COLOR = 'white';
export const QUANTIFIED_ONLY_COLORS = [...QNT_COLORS, NOT_DETECTED_COLOR];
export const ALL_COLORS = [...QNT_COLORS, TRACE_COLOR, NOT_DETECTED_COLOR];
export const ALL_AY_COLORS = [...QNT_COLORS, UNQUANTIFIED_COLOR, UNCLEAR_COLOR, NOT_DETECTED_COLOR];

const THRESHOLDS = {
  [NormalisationMode.SIXTEEN_S]: ['1', '0.1', '0.01', '1e-3', '1e-4', '1e-5'],
  [NormalisationMode.TEN_UL_DILUTED_DNA_16S]: ['1e7', '1e6', '1e5', '1e4', '1e3', '1e2'],
  [NormalisationMode.TEN_UL_DILUTED_DNA_AY]: ['1e7', '1e6', '1e5', '1e4', '1e3', '1e2'],
  [NormalisationMode.LITRE_16S]: ['1e9', '1e8', '1e7', '1e6', '1e5', '1e4'],
  [NormalisationMode.LITRE_AY]: ['1e9', '1e8', '1e7', '1e6', '1e5', '1e4'],
  [NormalisationMode.HOUR]: ['1e12', '1e11', '1e10', '1e9', '1e8', '1e7'],
  [NormalisationMode.MG_SS]: ['1e10', '1e9', '1e8', '1e7', '1e6', '1e5'],
  [NormalisationMode.MG_BOD]: ['1e10', '1e9', '1e8', '1e7', '1e6', '1e5'],
} as const;

const TRACES = 'traces';
export const UNQUANTIFIED_LABEL = 'detected below LOQ';
export const UNCLEAR_LABEL = 'unclear amplification';
const NOT_DETECTED = 'not detected';

function getBins(mode: NormalisationMode): string[] {
  const thresholds = THRESHOLDS[mode];
  const bins = thresholds.map(
    (t, i) => `≥ ${t}` + (mode === NormalisationMode.SIXTEEN_S && i === thresholds.length - 1 ? ' (LOD)' : ''),
  );
  return bins;
}

export function getBinLabels(normalisationMode: NormalisationMode, abundanceMode: AbunanceSelection): string[] {
  const bins = getBins(normalisationMode);
  if (abundanceMode === AbunanceSelection.QUANTIFIED_ONLY) {
    return [...bins, NOT_DETECTED];
  }
  return isAyNormalisationMode(normalisationMode)
    ? [...bins, UNQUANTIFIED_LABEL, UNCLEAR_LABEL, NOT_DETECTED]
    : [...bins, TRACES, NOT_DETECTED];
}

export const WORLDMAP_BINS = [...getBins(NormalisationMode.SIXTEEN_S), 'no data'];
export const WORLDMAP_COLORS = [...QNT_COLORS, oldTheme.faintGray];

export function getHeatmapLegendColors(
  normalisationMode: NormalisationMode,
  abundanceMode: AbunanceSelection,
): string[] {
  if (abundanceMode === AbunanceSelection.QUANTIFIED_ONLY) {
    return QUANTIFIED_ONLY_COLORS;
  }

  return isAyNormalisationMode(normalisationMode) ? ALL_AY_COLORS : ALL_COLORS;
}

export function shouldRenderUnclearTriangle(
  normalisationMode: NormalisationMode,
  flag: Flags | null,
  isWorldmap = false,
): boolean {
  return !isWorldmap && isAyNormalisationMode(normalisationMode) && flag === Flags.UNCLEAR;
}

export function shouldRenderUnclearLegendTriangle(normalisationMode: NormalisationMode, label: string): boolean {
  return isAyNormalisationMode(normalisationMode) && label === UNCLEAR_LABEL;
}

export function binColor(
  val: number | undefined | null,
  normalisationMode: NormalisationMode,
  isWorldmap = false,
  flag: Flags | null = null,
): string {
  const colors = isWorldmap ? WORLDMAP_COLORS : isAyNormalisationMode(normalisationMode) ? ALL_AY_COLORS : ALL_COLORS;
  const thresholds = THRESHOLDS[normalisationMode];

  const notDetectedColor = colors[colors.length - 1];
  const ayMode = isAyNormalisationMode(normalisationMode);
  const traceColor = isWorldmap
    ? colors[Math.max(0, colors.length - 3)]
    : ayMode
      ? UNQUANTIFIED_COLOR
      : TRACE_COLOR;

  if (ayMode && isUnclear(flag)) {
    return isWorldmap ? traceColor : UNCLEAR_COLOR;
  }
  if (ayMode && isUnquantified(flag)) {
    return traceColor;
  }
  if (isNonQuantifiedFlag(flag)) {
    return traceColor;
  }
  if (val === TRACES_VAL) {
    return traceColor;
  }
  if (val === undefined || val === null) {
    return notDetectedColor;
  }

  for (let i = 0; i < thresholds.length; i++) {
    if (val >= +thresholds[i]) {
      return colors[i];
    }
  }
  return notDetectedColor;
}
